import { useLanguage } from '@/contexts/LanguageContext';
import {
  fetchTalentDashboardStats,
  fetchTalentDashboardTalents,
  formatCompactNumber,
  transformDashboardStats,
  type TalentDashboardTalentItem,
  type TalentGroupedData,
  type TransformedDashboardResult
} from '@/lib/api/talent-stats';
import { getToken } from '@/lib/utils/auth-storage';
import { motion } from 'framer-motion';
import {
  AlertTriangle,
  Award,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  Database,
  ExternalLink,
  Flame,
  Globe,
  Layers,
  Loader2,
  Lock,
  Play,
  RefreshCw,
  Target,
  Users
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  PolarAngleAxis,
  PolarGrid,
  Radar,
  RadarChart,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts';

// 平台专属品牌色与标签映射
const PLATFORM_META: Record<string, { label: string; color: string; bg: string }> = {
  douyin: { label: '抖音', color: '#FE2C55', bg: 'rgba(254, 44, 85, 0.15)' },
  bilibili: { label: 'Bilibili', color: '#00AEEC', bg: 'rgba(0, 174, 236, 0.15)' },
  tiktok: { label: 'TikTok', color: '#00F2FE', bg: 'rgba(0, 242, 254, 0.15)' },
  xiaohongshu: { label: '小红书', color: '#FF2442', bg: 'rgba(255, 36, 66, 0.15)' },
  instagram: { label: 'Instagram', color: '#E1306C', bg: 'rgba(225, 48, 108, 0.15)' },
  youtube: { label: 'YouTube', color: '#FF0000', bg: 'rgba(255, 0, 0, 0.15)' },
};

/**
 * 达人大盘骨架屏（对齐生产环境 7 大图表布局与暗黑质感，彻底杜绝布局跳变 CLS）
 */
const TalentDashboardSkeleton: React.FC<{ isZh: boolean }> = ({ isZh }) => {
  return (
    <div className="animate-pulse space-y-8 mb-8" aria-busy="true" aria-label="Loading dashboard">
      {/* 1. 顶部指标区：月度可触达播放量 (5列) + 平台构成 (7列) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* 月度可触达播放量骨架 */}
        <div className="lg:col-span-5 p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06] flex flex-col justify-between min-h-[220px]">
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-white/[0.06]" />
                <div className="space-y-1.5">
                  <div className="w-28 h-4 rounded bg-white/[0.06]" />
                  <div className="w-44 h-3 rounded bg-white/[0.03]" />
                </div>
              </div>
              <div className="w-16 h-5 rounded-full bg-white/[0.04]" />
            </div>
            <div className="my-4 space-y-2">
              <div className="w-44 h-10 rounded-lg bg-white/[0.08]" />
              <div className="w-32 h-3 rounded bg-white/[0.04]" />
            </div>
          </div>
          <div className="pt-3 border-t border-white/[0.04] flex items-center justify-between">
            <div className="w-28 h-3 rounded bg-white/[0.04]" />
            <div className="w-20 h-3 rounded bg-white/[0.04]" />
          </div>
        </div>

        {/* 平台构成分布骨架 */}
        <div className="lg:col-span-7 p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06] flex flex-col justify-between min-h-[220px]">
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-white/[0.06]" />
                <div className="w-28 h-4 rounded bg-white/[0.06]" />
                <div className="w-24 h-3 rounded bg-white/[0.03]" />
              </div>
            </div>
            {/* 堆叠进度条骨架 */}
            <div className="w-full h-3 rounded-full bg-white/[0.04] my-3" />
            {/* 平台标签骨架条 */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 pt-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="p-2.5 rounded-xl border border-white/[0.04] bg-white/[0.015] space-y-2">
                  <div className="w-12 h-3 rounded bg-white/[0.05]" />
                  <div className="w-16 h-4 rounded bg-white/[0.07]" />
                  <div className="w-8 h-2.5 rounded bg-white/[0.03]" />
                </div>
              ))}
            </div>
          </div>
          <div className="pt-3 border-t border-white/[0.04] flex items-center justify-between">
            <div className="w-32 h-3 rounded bg-white/[0.04]" />
            <div className="w-20 h-3 rounded bg-white/[0.04]" />
          </div>
        </div>
      </div>

      {/* 2. 中间核心 KPI 卡片区 (4 项并列指标) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="p-5 rounded-2xl bg-white/[0.02] border border-white/[0.06] space-y-3">
            <div className="flex items-center justify-between">
              <div className="w-16 h-3 rounded bg-white/[0.05]" />
              <div className="w-4 h-4 rounded bg-white/[0.06]" />
            </div>
            <div className="w-24 h-7 rounded-lg bg-white/[0.08]" />
            <div className="w-20 h-3 rounded bg-white/[0.04]" />
          </div>
        ))}
      </div>

      {/* 3. 粉丝分级梯队 (7列) + 五维能力雷达图 (5列) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* 粉丝分级梯队柱状图骨架 */}
        <div className="lg:col-span-7 p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-white/[0.06]" />
              <div className="w-32 h-4 rounded bg-white/[0.06]" />
            </div>
            <div className="w-24 h-3 rounded bg-white/[0.04]" />
          </div>
          {/* 柱状条占位 */}
          <div className="h-64 flex items-end justify-around gap-4 px-4 pb-4 border-b border-white/[0.04]">
            <div className="w-12 h-20 rounded-t-lg bg-white/[0.04]" />
            <div className="w-12 h-36 rounded-t-lg bg-white/[0.05]" />
            <div className="w-12 h-52 rounded-t-lg bg-white/[0.07]" />
            <div className="w-12 h-40 rounded-t-lg bg-white/[0.05]" />
            <div className="w-12 h-24 rounded-t-lg bg-white/[0.04]" />
          </div>
          <div className="flex justify-around pt-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="w-10 h-3 rounded bg-white/[0.03]" />
            ))}
          </div>
        </div>

        {/* 五维能力雷达图骨架 */}
        <div className="lg:col-span-5 p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06] flex flex-col justify-between">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-white/[0.06]" />
              <div className="w-28 h-4 rounded bg-white/[0.06]" />
            </div>
            <div className="w-16 h-4 rounded-full bg-white/[0.04]" />
          </div>
          {/* 雷达图圆形网格骨架 */}
          <div className="h-64 flex items-center justify-center">
            <div className="w-44 h-44 rounded-full border border-dashed border-white/[0.08] flex items-center justify-center">
              <div className="w-28 h-28 rounded-full border border-dashed border-white/[0.06] flex items-center justify-center">
                <div className="w-12 h-12 rounded-full bg-white/[0.04]" />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-5 gap-2 pt-2 border-t border-white/[0.04]">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-8 rounded-lg bg-white/[0.02] border border-white/[0.04]" />
            ))}
          </div>
        </div>
      </div>

      {/* 4. 赛道分布横向条形图 (7列) + 数据覆盖审计矩阵 (5列) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06] space-y-4">
          <div className="flex items-center justify-between mb-2">
            <div className="w-32 h-4 rounded bg-white/[0.06]" />
            <div className="w-20 h-3 rounded bg-white/[0.04]" />
          </div>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-20 h-3 rounded bg-white/[0.04]" />
              <div className="flex-1 h-3.5 rounded-full bg-white/[0.03]" />
              <div className="w-10 h-3 rounded bg-white/[0.04]" />
            </div>
          ))}
        </div>

        <div className="lg:col-span-5 p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06] space-y-4">
          <div className="flex items-center justify-between mb-2">
            <div className="w-36 h-4 rounded bg-white/[0.06]" />
            <div className="w-16 h-3 rounded bg-white/[0.04]" />
          </div>
          <div className="space-y-3 pt-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-8 rounded-lg bg-white/[0.02] border border-white/[0.04]" />
            ))}
          </div>
        </div>
      </div>

      {/* 5. 粉丝量与互动率散点图骨架 */}
      <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06] space-y-4">
        <div className="flex items-center justify-between">
          <div className="space-y-1.5">
            <div className="w-48 h-4 rounded bg-white/[0.06]" />
            <div className="w-64 h-3 rounded bg-white/[0.03]" />
          </div>
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="w-12 h-6 rounded-lg bg-white/[0.04]" />
            ))}
          </div>
        </div>
        <div className="h-72 w-full rounded-xl bg-white/[0.015] border border-dashed border-white/[0.06] flex items-center justify-center">
          <div className="text-xs text-[#F2ECE5]/30">
            {isZh ? '正在加载对数散点分布与象限聚类...' : 'Loading log scatter distribution...'}
          </div>
        </div>
      </div>
    </div>
  );
};

export interface InfluencerLibraryPageProps {
  onRequestLogin?: () => void;
}

export const InfluencerLibraryPage: React.FC<InfluencerLibraryPageProps> = ({ onRequestLogin }) => {
  const { language } = useLanguage();
  const isZh = language === 'zh';

  // 散点图平台独立筛选状态
  const [scatterPlatform, setScatterPlatform] = useState<string>('all');

  // 大盘统计异步数据、加载中与错误状态（真实数据驱动，严禁静态假数据欺骗）
  const [dashboardData, setDashboardData] = useState<TransformedDashboardResult | null>(null);
  const [statsLoading, setStatsLoading] = useState<boolean>(true);
  const [statsError, setStatsError] = useState<string | null>(null);

  const statsData = dashboardData?.statsData ?? null;
  const scatterPoints = dashboardData?.scatterPoints ?? [];
  const coverageData = dashboardData?.coverageData ?? [];
  const totalPoints = dashboardData?.totalPoints;

  // 散点图平台从 scatterPoints (即 scatter_plot.points) 动态统计实际存在的平台列表与点位数量，按点位降序排序
  const availableScatterPlatforms = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const pt of scatterPoints) {
      if (pt.platform_flag) {
        counts[pt.platform_flag] = (counts[pt.platform_flag] || 0) + 1;
      }
    }
    return Object.keys(counts)
      .sort((a, b) => counts[b] - counts[a])
      .map((code) => {
        const meta = PLATFORM_META[code];
        return {
          code,
          label: meta?.label || code.charAt(0).toUpperCase() + code.slice(1),
          color: meta?.color || '#F97316',
          count: counts[code],
        };
      });
  }, [scatterPoints]);

  // 若当前选中的平台在新的散点数据集中已不存在，自动回退到 'all'
  React.useEffect(() => {
    if (scatterPlatform !== 'all' && !availableScatterPlatforms.some((p) => p.code === scatterPlatform)) {
      setScatterPlatform('all');
    }
  }, [scatterPlatform, availableScatterPlatforms]);

  // 过滤后的散点数据集
  const filteredScatterPoints = useMemo(() => {
    if (scatterPlatform === 'all') return scatterPoints;
    return scatterPoints.filter((p) => p.platform_flag === scatterPlatform);
  }, [scatterPlatform, scatterPoints]);

  // 达人明细列表分页与检索状态（对接真实接口 GET /med/talent/dashboard/talents）
  const [previewPlatform, setPreviewPlatform] = useState<string>('all');
  const [previewCategory, setPreviewCategory] = useState<string>('all');
  const [previewTier, setPreviewTier] = useState<string>('all');
  const [previewSearch, setPreviewSearch] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');

  const [talentsList, setTalentsList] = useState<TalentDashboardTalentItem[]>([]);
  const [groupedData, setGroupedData] = useState<TalentGroupedData | null>(null);
  const [isUnlocked, setIsUnlocked] = useState<boolean>(false);
  const [talentsTotal, setTalentsTotal] = useState<number>(0);
  const [talentsPage, setTalentsPage] = useState<number>(1);
  const [talentsLoading, setTalentsLoading] = useState<boolean>(true);
  const [talentsError, setTalentsError] = useState<string | null>(null);

  // 搜索关键字 400ms 防抖
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(previewSearch.trim());
    }, 400);
    return () => clearTimeout(timer);
  }, [previewSearch]);

  const activeTalentReqRef = React.useRef(0);

  // 异步拉取达人列表或试看样本
  const loadTalents = useCallback(async (targetPage = 1) => {
    const reqId = ++activeTalentReqRef.current;
    setTalentsLoading(true);
    setTalentsError(null);

    try {
      const res = await fetchTalentDashboardTalents({
        page: targetPage,
        size: 20,
        platform: previewPlatform !== 'all' ? previewPlatform : undefined,
        category: previewCategory !== 'all' ? previewCategory : undefined,
        follower_bucket: previewTier !== 'all' ? previewTier : undefined,
        keyword: previewSearch.trim() || undefined,
        sort_field: 'follower_count',
        sort_order: 'desc',
      });

      if (reqId !== activeTalentReqRef.current) return;

      const unlocked = Boolean(res.unlocked);
      setIsUnlocked(unlocked);

      if (unlocked && res.page_data) {
        // 管理员全量分页模式
        setTalentsList(res.page_data.list || []);
        setTalentsTotal(res.page_data.total || 0);
        setTalentsPage(targetPage);
        setGroupedData(null);
      } else if (!unlocked && res.grouped_data) {
        // 未登录 / 普通访客试看模式（4 个量级各 20 条，共 80 条优质样本）
        setGroupedData(res.grouped_data);
        const totalSample =
          (res.grouped_data.under_10k?.length || 0) +
          (res.grouped_data.from_10k_to_100k?.length || 0) +
          (res.grouped_data.from_100k_to_1m?.length || 0) +
          (res.grouped_data.over_1m?.length || 0);
        setTalentsTotal(totalSample);
        setTalentsList([]);
        setTalentsPage(1);
      } else {
        setTalentsList(res.page_data?.list || []);
        setTalentsTotal(res.page_data?.total || 0);
        setTalentsPage(targetPage);
        setGroupedData(null);
      }
    } catch (err: any) {
      if (reqId !== activeTalentReqRef.current) return;
      console.error('[TalentDashboard] 达人列表查询失败:', err);
      setTalentsError(err?.message || 'FAILED');
      setTalentsList([]);
      setGroupedData(null);
      setTalentsTotal(0);
    } finally {
      if (reqId === activeTalentReqRef.current) {
        setTalentsLoading(false);
      }
    }
  }, [previewPlatform, previewCategory, previewTier, previewSearch]);

  // 筛选条件或防抖关键词变化时自动触发搜索（重置为第一页）
  useEffect(() => {
    loadTalents(1);
  }, [previewPlatform, previewCategory, previewTier, debouncedSearch]);

  // 立即搜索提交（回车或点击搜索按钮）
  const handleSearchSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    loadTalents(1);
  };

  // 计算当前最终展示的达人列表（未登录试看模式下自适应按分段或全部呈现）
  const displayedTalents = useMemo<TalentDashboardTalentItem[]>(() => {
    if (isUnlocked) {
      return talentsList;
    }
    if (!groupedData) {
      return talentsList;
    }
    if (previewTier === '<10k') return groupedData.under_10k || [];
    if (previewTier === '10k-100k') return groupedData.from_10k_to_100k || [];
    if (previewTier === '100k-1M') return groupedData.from_100k_to_1m || [];
    if (previewTier === '>1M') return groupedData.over_1m || [];

    // 'all' -> 全部 4 组样板平铺（按互动率降序各 20 条，共 80 条）
    return [
      ...(groupedData.under_10k || []),
      ...(groupedData.from_10k_to_100k || []),
      ...(groupedData.from_100k_to_1m || []),
      ...(groupedData.over_1m || []),
    ];
  }, [isUnlocked, talentsList, groupedData, previewTier]);

  const handleTriggerLogin = () => {
    onRequestLogin?.();
  };

  const activeRequestIdRef = React.useRef(0);

  // 调用后端真实大盘接口 (GET /api/med/talent/dashboard/stats)
  const loadDashboardStats = React.useCallback(async () => {
    const currentId = ++activeRequestIdRef.current;
    setStatsLoading(true);
    setStatsError(null);

    try {
      const raw = await fetchTalentDashboardStats();
      if (currentId !== activeRequestIdRef.current) return;
      const transformed = transformDashboardStats(raw);
      setDashboardData(transformed);
    } catch (err: any) {
      if (currentId !== activeRequestIdRef.current) return;
      console.error('[TalentDashboard] 接口调用异常:', err);
      setStatsError(err?.message || 'FAILED');
      setDashboardData(null);
    } finally {
      if (currentId === activeRequestIdRef.current) {
        setStatsLoading(false);
      }
    }
  }, []);

  // 页面挂载时拉取大盘统计数据
  React.useEffect(() => {
    loadDashboardStats();
    return () => {
      // 组件卸载或重新挂载时作废正在进行的请求，杜绝严格模式双重执行引发的连续闪烁
      activeRequestIdRef.current++;
    };
  }, [loadDashboardStats]);

  // 登录与商业数据鉴权状态（基于系统存储 Token，支持同页广播、跨标签页与窗口聚焦多源实时同步，登录/切换账号时自动无刷新重载全量数据）
  const [authToken, setAuthToken] = useState<string | null>(() => getToken());
  const isLoggedIn = Boolean(authToken);
  const lastKnownTokenRef = React.useRef<string | null>(authToken);

  React.useEffect(() => {
    const handleAuthChange = (e?: Event) => {
      const latestToken = getToken();
      const isExplicitAuthEvent = e?.type === 'auth-state-change';

      // 发生明确认证变动或检测到 token 变动时，实时更新鉴权状态并重新拉取全量数据
      if (isExplicitAuthEvent || latestToken !== lastKnownTokenRef.current) {
        lastKnownTokenRef.current = latestToken;
        setAuthToken(latestToken);
        loadTalents(1);
        loadDashboardStats();
      }
    };

    window.addEventListener('storage', handleAuthChange);
    window.addEventListener('auth-state-change', handleAuthChange);
    window.addEventListener('focus', handleAuthChange);

    return () => {
      window.removeEventListener('storage', handleAuthChange);
      window.removeEventListener('auth-state-change', handleAuthChange);
      window.removeEventListener('focus', handleAuthChange);
    };
  }, [loadTalents, loadDashboardStats]);

  // 粉丝分桶层级定位（梯队定位与峰值标记）
  const bucketTiers = useMemo(() => {
    if (!statsData?.follower_bucket_distribution) return [];
    const tierMap: Record<string, { zh: string; en: string }> = {
      '<1k': { zh: '新星潜苗', en: 'Seed' },
      '1k-10k': { zh: '长尾KOC', en: 'Nano' },
      '10k-100k': { zh: '核心腰部', en: 'Mid-tier' },
      '100k-1M': { zh: '头部精英', en: 'Macro' },
      '>1M': { zh: '顶流大V', en: 'Top Tier' },
    };
    const maxCount = Math.max(...(statsData.follower_bucket_distribution.map((d) => d.count) || [1]), 1);

    return statsData.follower_bucket_distribution.map((item) => ({
      ...item,
      tierName: isZh ? (tierMap[item.bucket_code]?.zh ?? item.bucket_label ?? item.bucket_name ?? item.bucket_code) : (tierMap[item.bucket_code]?.en ?? item.bucket_label ?? item.bucket_name ?? item.bucket_code),
      isMax: item.count === maxCount,
    }));
  }, [statsData?.follower_bucket_distribution, isZh]);

  // 五维投放能力雷达图数据（中英文国际化与综合均分）
  const radarData = useMemo(() => {
    if (!statsData?.radar_dimensions?.dimensions) return { dimensions: [], avgScore: '0' };
    const dimMap: Record<string, { zh: string; en: string }> = {
      scale: { zh: '规模', en: 'Scale' },
      engagement: { zh: '互动', en: 'Engagement' },
      cost: { zh: '性价比', en: 'Value' },
      activity: { zh: '活跃', en: 'Activity' },
      reachability: { zh: '可触达', en: 'Reach' },
    };

    const list = statsData.radar_dimensions.dimensions.map((d) => ({
      ...d,
      displayName: isZh ? (dimMap[d.key]?.zh ?? d.name) : (dimMap[d.key]?.en ?? d.name),
    }));

    const totalScore = list.reduce((acc, curr) => acc + curr.score, 0);
    const avgScore = list.length > 0 ? (totalScore / list.length).toFixed(1) : '0';

    return {
      dimensions: list,
      avgScore,
    };
  }, [statsData?.radar_dimensions, isZh]);

  // 自定义雷达图外圈角标签（维度名 + 高亮具体数值，彻底替代丑陋的斜向刻度轴）
  const renderRadarTick = (props: any) => {
    const { x, y, cx, cy, index } = props;
    const item = radarData.dimensions[index];
    if (!item) return null;

    const dx = x - cx;
    const dy = y - cy;
    let textAnchor: 'start' | 'middle' | 'end' = 'middle';
    if (Math.abs(dx) > 18) {
      textAnchor = dx > 0 ? 'start' : 'end';
    }

    const offsetY = dy < -20 ? -12 : dy > 20 ? 12 : 0;
    const offsetX = dx > 20 ? 6 : dx < -20 ? -6 : 0;

    return (
      <g transform={`translate(${x + offsetX}, ${y + offsetY})`}>
        <text
          x={0}
          y={0}
          textAnchor={textAnchor}
          fill="#F2ECE5"
          opacity={0.65}
          fontSize={11}
          fontWeight={500}
        >
          {item.displayName}
        </text>
        <text
          x={0}
          y={13}
          textAnchor={textAnchor}
          fill="#FDBA74"
          fontSize={11}
          fontWeight={700}
          fontFamily="monospace"
        >
          {item.score}
        </text>
      </g>
    );
  };

  return (
    <div className="min-h-screen pt-28 pb-24 bg-[#0B0907] text-[#F2ECE5]">
      {/* 顶部光晕背景装饰 */}
      <div className="relative overflow-hidden pointer-events-none">
        <div className="absolute top-[-100px] left-1/2 -translate-x-1/2 w-[800px] h-[360px] bg-gradient-to-b from-[#F97316]/20 via-[#F97316]/5 to-transparent blur-[130px] rounded-full" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* 页面主标题与导语 */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8 pb-6 border-b border-white/[0.08]">
          <div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#F2ECE5]">
              {isZh ? '达人资源库' : 'Creator Library'}
            </h1>
            <p className="mt-3 text-sm sm:text-base text-[#F2ECE5]/65 max-w-3xl leading-relaxed">
              {isZh
                ? '全库达人多维资产全景概览。聚合月度可触达曝光体量、全渠道平台构成比率、粉丝分级梯队与五维投放能力雷达，驱动精准触达与爆款策略。'
                : 'Panoramic cross-channel creator intelligence. Aggregates monthly reachable play volume, platform share, follower bucket distributions, and 5-dimension radar scoring.'}
            </p>
          </div>
        </div>

        {/* 加载中状态骨架屏 (1:1 结构还原，杜绝页面跳变) */}
        {statsLoading && !statsData && (
          <motion.div
            key="dashboard-skeleton"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <TalentDashboardSkeleton isZh={isZh} />
          </motion.div>
        )}

        {/* 数据加载失败状态（与加载态互斥，平滑淡入） */}
        {statsError && !statsLoading && !statsData && (
          <motion.div
            key="dashboard-error"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            className="mb-8 p-8 rounded-2xl bg-white/[0.02] border border-white/[0.08] flex flex-col items-center justify-center text-center"
          >
            <div className="w-10 h-10 rounded-2xl bg-white/[0.04] border border-white/[0.08] flex items-center justify-center mb-3">
              <AlertTriangle className="w-5 h-5 text-amber-400/80" />
            </div>
            <h4 className="text-sm font-medium text-[#F2ECE5]">
              {isZh ? '数据加载失败' : 'Failed to load data'}
            </h4>
            <p className="text-xs text-[#F2ECE5]/50 mt-1.5 max-w-sm">
              {isZh
                ? '暂未能获取到最新大盘统计数据，请检查网络后重新加载'
                : 'Unable to retrieve dashboard statistics. Please check your connection and reload.'}
            </p>
            <button
              type="button"
              onClick={loadDashboardStats}
              className="mt-4 px-4 py-2 rounded-xl bg-white/[0.06] hover:bg-white/[0.12] border border-white/10 text-xs font-medium text-[#F2ECE5] flex items-center gap-1.5 transition-all cursor-pointer"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${statsLoading ? 'animate-spin' : ''}`} />
              <span>{isZh ? '重新加载' : 'Reload'}</span>
            </button>
          </motion.div>
        )}

        {/* 仅在后端真实数据加载成功后，才渲染大盘 6 大看板组件 */}
        {statsData && (
          <>
            {/* ============================================================== */}
            {/* 组件 1 & 组件 2：顶部指标区 (月度可触达播放量 + 平台构成)       */}
            {/* ============================================================== */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
          {/* 组件 1：月度可触达播放量 (占 5 列) */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="lg:col-span-5 p-6 rounded-2xl bg-gradient-to-br from-[#F97316]/10 via-white/[0.02] to-transparent border border-white/[0.08] relative overflow-hidden flex flex-col justify-between group hover:border-[#F97316]/40 transition-all"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-[#F97316]/15 border border-[#F97316]/30 flex items-center justify-center">
                  <Play className="w-4 h-4 text-[#F97316] fill-[#F97316]" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-[#F2ECE5]">
                    {isZh ? '月度可触达播放量' : 'Monthly Reachable Play Volume'}
                  </h3>
                  <p className="text-[11px] text-[#F2ECE5]/50">
                    {isZh ? '库内具备有效联络渠道达人的月均总曝光' : 'Total monthly video views of reachable creators'}
                  </p>
                </div>
              </div>
              <span className="text-xs text-[#FDBA74] font-medium shrink-0">
                {isZh ? '高价值曝光' : 'High Value'}
              </span>
            </div>

            <div className="my-3">
              <div className="flex items-baseline gap-2">
                <span className="text-4xl sm:text-5xl font-extrabold tracking-tight text-[#F2ECE5]">
                   {statsData.monthly_reachable_play.count.toLocaleString()}    
                </span>
                <span className="text-sm font-medium text-[#FDBA74]">
                 {isZh ? '次' : 'impressions'}
                </span>
              </div>
              <div className="text-xs text-[#F2ECE5]/40 mt-2 font-mono">
                {isZh ? '预计曝光量: ' : 'Estimated impressions: '}
                {formatCompactNumber(statsData.monthly_reachable_play.count, language)}
              </div>
            </div>
          </motion.div>

          {/* 组件 2：平台构成 (占 7 列) */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="lg:col-span-7 p-6 rounded-2xl bg-white/[0.02] border border-white/[0.08] flex flex-col justify-between"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Layers className="w-4 h-4 text-[#F97316]" />
                <h3 className="text-sm font-semibold text-[#F2ECE5]">
                  {isZh ? '平台构成分布' : 'Platform Share'}
                </h3>
                <span className="text-xs text-[#F2ECE5]/40">
                  ({isZh ? '有效账号总数' : 'Total Creators'}:{' '}
                  <strong className="text-[#F2ECE5] font-bold">
                    {statsData.platform_composition.total_account_count.toLocaleString()}
                  </strong>
                  )
                </span>
              </div>
            </div>

            {/* 横向多色堆叠进度条 */}
            <div className="w-full h-3 rounded-full overflow-hidden flex bg-white/[0.05] p-0.5 gap-0.5 my-3">
              {statsData.platform_composition.list.map((p) => {
                const meta = PLATFORM_META[p.code] || { label: p.name || p.code, color: '#F97316', bg: 'rgba(249,115,22,0.15)' };
                return (
                  <div
                    key={p.code}
                    className="h-full rounded-sm transition-all duration-300 relative"
                    style={{
                      width: `${p.percentage}%`,
                      backgroundColor: meta.color,
                      minWidth: p.percentage > 0 ? '6px' : '0',
                    }}
                    title={`${p.name || meta.label}: ${p.count} (${p.percentage}%)`}
                  />
                );
              })}
            </div>

            {/* 各平台分布详情标签条 */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 pt-2">
              {statsData.platform_composition.list.map((p) => {
                const meta = PLATFORM_META[p.code] || { label: p.name || p.code, color: '#F97316', bg: 'rgba(255,255,255,0.05)' };
                return (
                  <div
                    key={p.code}
                    className="p-2.5 rounded-xl border border-white/[0.06] bg-white/[0.02]"
                  >
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: meta.color }} />
                      <span className="text-xs font-medium text-[#F2ECE5] truncate">{meta.label}</span>
                    </div>
                    <div className="text-sm font-bold text-[#F2ECE5]">{p.count.toLocaleString()}</div>
                    <div className="text-[11px] text-[#F2ECE5]/50">{p.percentage}%</div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </div>

        {/* ============================================================== */}
        {/* 组件 3：中间核心 KPI 卡片区 (4 项并列指标)                      */}
        {/* ============================================================== */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {/* KPI 1: 账号数 */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-5 rounded-2xl bg-white/[0.03] border border-white/[0.08] hover:border-[#F97316]/40 transition-all"
          >
            <div className="flex items-center justify-between text-xs text-[#F2ECE5]/60 mb-2">
              <span>{isZh ? '达人账号数' : 'Total Creators'}</span>
              <Users className="w-4 h-4 text-[#F97316]" />
            </div>
            <div className="text-2xl sm:text-3xl font-extrabold text-[#F2ECE5]">
              {statsData.kpi_summary.account_count.toLocaleString()}
            </div>
            <div className="mt-2 text-xs text-[#FDBA74] flex items-center gap-1">
              <span>{isZh ? '占全库' : 'Share:'}</span>
              <strong className="font-semibold">{statsData.kpi_summary.account_share_percentage}%</strong>
            </div>
          </motion.div>

          {/* KPI 2: 粉丝总量 */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="p-5 rounded-2xl bg-white/[0.03] border border-white/[0.08] hover:border-[#F97316]/40 transition-all"
          >
            <div className="flex items-center justify-between text-xs text-[#F2ECE5]/60 mb-2">
              <span>{isZh ? '粉丝总量' : 'Total Followers'}</span>
              <Globe className="w-4 h-4 text-[#F97316]" />
            </div>
            <div className="text-2xl sm:text-3xl font-extrabold text-[#F2ECE5]">
               {statsData.kpi_summary.total_follower_count.toLocaleString()}
            </div>
            <div className="mt-2 text-xs text-[#F2ECE5]/40 font-mono truncate">
               {formatCompactNumber(statsData.kpi_summary.total_follower_count, language)}
            </div>
          </motion.div>

          {/* KPI 3: 粉丝中位数 */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="p-5 rounded-2xl bg-white/[0.03] border border-white/[0.08] hover:border-[#F97316]/40 transition-all"
          >
            <div className="flex items-center justify-between text-xs text-[#F2ECE5]/60 mb-2">
              <span>{isZh ? '粉丝中位数' : 'Median Followers'}</span>
              <Flame className="w-4 h-4 text-[#F97316]" />
            </div>
            <div className="text-2xl sm:text-3xl font-extrabold text-[#F2ECE5]">
              {formatCompactNumber(statsData.kpi_summary.median_follower_count, language)}
            </div>
            <div className="mt-2 text-xs text-emerald-400 flex items-center gap-1">
              <span>{isZh ? '体量中轴锚点' : 'Median Benchmark'}</span>
            </div>
          </motion.div>

          {/* KPI 4: 平均综合评分 */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="p-5 rounded-2xl bg-white/[0.03] border border-white/[0.08] hover:border-[#F97316]/40 transition-all"
          >
            <div className="flex items-center justify-between text-xs text-[#F2ECE5]/60 mb-2">
              <span>{isZh ? '平均综合评分' : 'Overall Score'}</span>
              <Award className="w-4 h-4 text-[#F97316]" />
            </div>
            <div className="text-2xl sm:text-3xl font-extrabold ">
              {statsData.kpi_summary.overall_score}
            </div>
            <div className="mt-2 text-xs text-[#F2ECE5]/50 flex items-center gap-1">
              <span>{isZh ? '基准满分' : 'Scale:'}</span>
              <strong>{statsData.kpi_summary.score_base}</strong>
            </div>
          </motion.div>
        </div>

        {/* ============================================================== */}
        {/* 组件 4、5、6：底部三大图表看板 (量级分布 + 五维雷达 + 赛道榜单)  */}
        {/* ============================================================== */}
        {/* 组件 4 & 5：左右对称双图表（粉丝分桶 + 五维雷达）            */}
        {/* ============================================================== */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8 items-stretch">
          {/* 组件 4：粉丝量级分布 (平分对称) */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.08] flex flex-col justify-between"
          >
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-[#F97316]" />
                  <h3 className="text-sm font-semibold text-[#F2ECE5]">
                    {isZh ? '粉丝量级分布' : 'Follower Distribution'}
                  </h3>
                  <span className="text-xs text-[#F2ECE5]/40 hidden sm:inline">
                    ({isZh ? '各量级梯队达人数量占比' : 'Creator tier share'})
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs text-[#F2ECE5]/45">
                  <span className="text-[#FDBA74] font-medium">{isZh ? '腰部为主力' : 'Mid-tier Dominant'}</span>
                  <span>·</span>
                  <span>{isZh ? '5档分桶' : '5 Buckets'}</span>
                </div>
              </div>
            </div>

            {/* 增强柱状图 (带渐变发光与胶囊轨道底槽，常显数据无悬浮遮挡) */}
            <div className="h-72 w-full my-2">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={bucketTiers}
                  margin={{ top: 28, right: 16, left: -10, bottom: 6 }}
                >
                  <defs>
                    <linearGradient id="followerBarNormal" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#EA580C" stopOpacity={0.9} />
                      <stop offset="100%" stopColor="#EA580C" stopOpacity={0.2} />
                    </linearGradient>
                    <linearGradient id="followerBarHighlight" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#FDBA74" stopOpacity={1} />
                      <stop offset="45%" stopColor="#F97316" stopOpacity={0.9} />
                      <stop offset="100%" stopColor="#EA580C" stopOpacity={0.3} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#ffffff0d" vertical={false} />
                  <XAxis
                    dataKey="bucket_label"
                    stroke="#ffffff40"
                    fontSize={12}
                    tickLine={false}
                    axisLine={{ stroke: '#ffffff15' }}
                  />
                  <YAxis
                    stroke="#ffffff40"
                    fontSize={11}
                    tickLine={false}
                    axisLine={{ stroke: '#ffffff15' }}
                  />
                  <Bar
                    dataKey="count"
                    radius={[8, 8, 2, 2]}
                    barSize={38}
                    background={{ fill: 'rgba(255, 255, 255, 0.035)', radius: 8 }}
                  >
                    {bucketTiers.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.isMax ? 'url(#followerBarHighlight)' : 'url(#followerBarNormal)'}
                      />
                    ))}
                    <LabelList
                      dataKey="percentage"
                      position="top"
                      content={(props: any) => {
                        const { x, y, width, value } = props;
                        if (value === undefined || value === null) return null;
                        const num = Number(value);
                        const text = num > 0 ? (num < 0.1 ? '<0.1%' : `${num.toFixed(1)}%`) : '0%';
                        return (
                          <text
                            x={Number(x) + Number(width) / 2}
                            y={Number(y) - 8}
                            fill={num > 50 ? '#FDBA74' : '#F2ECE5aa'}
                            textAnchor="middle"
                            fontSize={11}
                            fontWeight={600}
                            fontFamily="monospace"
                          >
                            {text}
                          </text>
                        );
                      }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* 5 档梯队生态明细卡片条（高度与右侧五维指标严格对齐 h-[66px]） */}
            <div className="grid grid-cols-5 gap-1.5 pt-2.5 border-t border-white/[0.06]">
              {bucketTiers.map((b) => (
                <div
                  key={b.bucket_code}
                  className={`h-[66px] p-2 rounded-lg border text-center flex flex-col justify-between transition-all ${
                    b.isMax
                      ? 'bg-[#F97316]/5 border-[#F97316]/20'
                      : 'bg-white/[0.015] border-white/[0.04]'
                  }`}
                >
                  <div className="text-[10px] text-[#F2ECE5]/45 truncate leading-tight">
                    {b.tierName}
                  </div>
                  <div className="text-[11px] font-medium text-[#F2ECE5]/80 leading-tight">
                    {b.bucket_label}
                  </div>
                  <div className="text-[11px] font-mono font-semibold text-[#FDBA74] leading-tight">
                    {b.count.toLocaleString()}
                    <span className="text-[9px] font-normal text-[#F2ECE5]/40 ml-0.5">
                      ({b.percentage}%)
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* 组件 5：五维平均分 (雷达图 - 平分对称) */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.08] flex flex-col justify-between"
          >
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Flame className="w-4 h-4 text-[#F97316]" />
                  <h3 className="text-sm font-semibold text-[#F2ECE5]">
                    {isZh ? '五维投放平均分' : '5-Dimension Radar Score'}
                  </h3>
                  <span className="text-xs text-[#F2ECE5]/40 hidden sm:inline">
                    ({isZh ? '综合能力矩阵评估' : 'Dimension rating'})
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs text-[#F2ECE5]/45">
                  <span className="text-[#FDBA74] font-medium font-mono">
                    {isZh ? '综合均分' : 'Avg'}: {radarData.avgScore}
                  </span>
                  <span>·</span>
                  <span>{isZh ? '满分 100' : 'Max 100'}</span>
                </div>
              </div>
            </div>

            <div className="h-72 w-full flex items-center justify-center my-2">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart
                  outerRadius={80}
                  data={radarData.dimensions}
                  margin={{ top: 16, right: 28, bottom: 16, left: 28 }}
                >
                  <defs>
                    <radialGradient id="radarFillGradient" cx="50%" cy="50%" r="50%">
                      <stop offset="0%" stopColor="#FDBA74" stopOpacity={0.7} />
                      <stop offset="60%" stopColor="#F97316" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#EA580C" stopOpacity={0.15} />
                    </radialGradient>
                  </defs>
                  <PolarGrid stroke="#ffffff14" gridType="polygon" />
                  <PolarAngleAxis
                    dataKey="displayName"
                    tick={renderRadarTick}
                  />
                  <Radar
                    name={isZh ? '五维得分' : 'Score'}
                    dataKey="score"
                    stroke="#F97316"
                    strokeWidth={2}
                    fill="url(#radarFillGradient)"
                    fillOpacity={0.7}
                    dot={{
                      r: 3.5,
                      fill: '#F97316',
                      stroke: '#FFFFFF',
                      strokeWidth: 1.5,
                    }}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>

            {/* 五维具体指标（高度与左侧分桶卡片严格对齐 h-[66px]） */}
            <div className="grid grid-cols-5 gap-1.5 pt-2.5 border-t border-white/[0.06]">
              {radarData.dimensions.map((d) => {
                const isHighlight = d.key === 'reachability' && d.score >= 80;
                return (
                  <div
                    key={d.key}
                    className={`h-[66px] p-2 rounded-lg border text-center flex flex-col justify-between transition-all ${
                      isHighlight
                        ? 'bg-emerald-500/5 border-emerald-500/25'
                        : 'bg-white/[0.015] border-white/[0.04]'
                    }`}
                  >
                    <div className="text-[10px] text-[#F2ECE5]/45 truncate leading-tight">
                      {d.displayName}
                    </div>
                    <div
                      className={`text-[12px] font-bold font-mono leading-tight ${
                        isHighlight ? 'text-emerald-400' : 'text-[#FDBA74]'
                      }`}
                    >
                      {d.score}
                      <span className="text-[9px] font-normal text-[#F2ECE5]/40 ml-0.5 font-sans">
                        / 100
                      </span>
                    </div>
                    {/* 2px 极细能力饱和度微刻度槽 */}
                    <div className="w-full bg-white/[0.08] h-[2px] rounded-full overflow-hidden mb-0.5">
                      <div
                        className={`h-full rounded-full transition-all duration-300 ${
                          isHighlight
                            ? 'bg-emerald-400'
                            : 'bg-gradient-to-r from-[#EA580C] to-[#FDBA74]'
                        }`}
                        style={{ width: `${Math.min(100, d.score)}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </div> 

        {/* ============================================================== */}
        {/* 组件 6：双子看板（并列一行）：赛道分布排行榜 + 各平台数据覆盖度质量审计 */}
        {/* ============================================================== */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8 items-stretch">
          {/* 左侧：赛道分布排行榜 (规范对齐：品牌橙体系、规范字体与icon) */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.08] flex flex-col justify-between"
          >
            <div>
              {/* 头部标题区 */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Layers className="w-4 h-4 text-[#F97316]" />
                  <h3 className="text-sm font-semibold text-[#F2ECE5]">
                    {isZh ? '赛道分布排行榜' : 'Category Distribution'}
                  </h3>
                  <span className="text-xs text-[#F2ECE5]/40 hidden sm:inline">
                    ({isZh ? '垂直赛道达人构成' : 'Creator share by category'})
                  </span>
                </div> 
              </div>

              {/* 10 个垂直赛道横向条形图 */}
              <div className="space-y-3 pr-1 pt-1">
                {statsData.track_distribution.map((item) => {
                  const maxCount = 2375;
                  const barWidthPercent = Math.max(3, Math.round((item.count / maxCount) * 100));

                  return (
                    <div key={item.category_code} className="flex items-center text-xs">
                      {/* 赛道名称 (右对齐) */}
                      <div className="w-24 sm:w-28 text-right text-[#F2ECE5]/70 pr-3.5 shrink-0 truncate font-medium text-xs">
                        {item.category_name}
                      </div>

                      {/* 胶囊轨道与品牌橙渐变条 */}
                      <div className="flex-1 flex items-center gap-3">
                        <div className="flex-1 h-3.5 bg-white/[0.03] rounded-full overflow-hidden relative">
                          <div
                            className="h-full rounded-full bg-gradient-to-r from-[#EA580C] to-[#F97316] transition-all duration-500 hover:brightness-110"
                            style={{ width: `${barWidthPercent}%` }}
                          />
                        </div>
                        <span className="font-mono text-xs text-[#FDBA74] shrink-0 font-medium w-12 text-right">
                          {item.count.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
 
          </motion.div>

          {/* 右侧：各平台数据覆盖度质量审计矩阵 (规范对齐、去花降噪、极简高级暗黑质感) */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.08] flex flex-col justify-between"
          >
            <div>
              {/* 头部标题与低调警示标签 */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Database className="w-4 h-4 text-[#F97316]" />
                  <h3 className="text-sm font-semibold text-[#F2ECE5]">
                    {isZh ? '各平台数据覆盖度质量审计' : 'Data Coverage Quality Audit'}
                  </h3>
                  <span className="text-xs text-[#F2ECE5]/40 hidden sm:inline">
                    ({isZh ? '跨平台维度完整性与可用性' : 'Completeness & health'})
                  </span>
                </div>
                
              </div>

              {/* 极简高级纯净审计表格（无杂乱进度条与霓虹小圆点） */}
              <div className="overflow-x-auto [scrollbar-width:thin] pt-1">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-white/[0.08] text-xs text-[#F2ECE5]/45 font-medium">
                      <th className="py-3 px-3 font-medium">{isZh ? '统计指标维度' : 'Dimension'}</th>
                      {coverageData.map((col) => (
                        <th
                          key={col.platform_flag}
                          className="py-3 px-2.5 text-center font-medium text-[#F2ECE5]/75 text-xs"
                        >
                          {col.platform_name}
                        </th>
                      ))}
                     
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/[0.04] text-xs">
                    {/* 行 1：账号储备数 */}
                    <tr className="hover:bg-white/[0.015] transition-colors">
                      <td className="py-3 px-3 text-[#F2ECE5]/70 font-medium">
                        {isZh ? '账号总数' : 'Total Accounts'}
                      </td>
                      {coverageData.map((col) => (
                        <td
                          key={col.platform_flag}
                          className="py-3 px-2.5 text-center font-mono"
                        >
                          <div className="text-xs font-semibold text-[#F2ECE5]">{col.total_accounts.toLocaleString()}</div>
                          <div className="text-[10px] text-[#F2ECE5]/40 mt-0.5">{col.account_share_pct}%</div>
                        </td>
                      ))} 
                    </tr>

                    {/* 行 2：地区覆盖率 */}
                    <tr className="hover:bg-white/[0.015] transition-colors">
                      <td className="py-3 px-3 text-[#F2ECE5]/70 font-medium">
                        {isZh ? '地区覆盖率' : 'Region Cov'}
                      </td>
                      {coverageData.map((col) => {
                        const isTikTokAlert = col.platform_flag === 'tiktok';
                        return (
                          <td
                            key={col.platform_flag}
                            className="py-3 px-2.5 text-center font-mono"
                          >
                            <span
                              className={`text-xs ${
                                isTikTokAlert
                                  ? 'text-amber-400 font-semibold underline decoration-dashed underline-offset-2'
                                  : 'text-[#F2ECE5]/80'
                              }`}
                            >
                              {col.region_coverage_pct}%
                            </span>
                          </td>
                        );
                      })} 
                    </tr>

                    {/* 行 3：赛道覆盖率 */}
                    <tr className="hover:bg-white/[0.015] transition-colors">
                      <td className="py-3 px-3 text-[#F2ECE5]/70 font-medium">
                        {isZh ? '赛道覆盖率' : 'Track Cov'}
                      </td>
                      {coverageData.map((col) => (
                        <td
                          key={col.platform_flag}
                          className="py-3 px-2.5 text-center font-mono text-xs text-[#F2ECE5]/80"
                        >
                          {col.track_coverage_pct}%
                        </td>
                      ))} 
                    </tr>

                    {/* 行 4：联系方式覆盖率 */}
                    {/* <tr className="hover:bg-white/[0.015] transition-colors">
                      <td className="py-3 px-3 text-[#F2ECE5]/70 font-medium">
                        {isZh ? '可触达率' : 'Reachability'}
                      </td>
                      {coverageData.map((col) => (
                        <td
                          key={col.platform_flag}
                          className="py-3 px-2.5 text-center font-mono text-xs text-[#F2ECE5]/80"
                        >
                          {col.contact_coverage_pct}%
                        </td>
                      ))} 
                    </tr> */}
                  </tbody>
                </table>
              </div>
            </div>
 
          </motion.div>
        </div>
      </>
    )}

        {/* ============================================================== */}
        {/* 组件 6.5：达人样本预览与检索组件 (对齐原图规范)               */}
        {/* ============================================================== */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.12 }}
          className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.08] mb-8"
        >
          {/* 顶部多维筛选与搜索条 (平台 + 赛道 + 粉丝量级 + 搜索框 + 搜索按钮) */}
          <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-3 pb-5 border-b border-white/[0.06]">
            {/* 平台筛选 */}
            {/* <div className="flex items-center gap-2 bg-white/[0.03] border border-white/[0.08] px-3.5 py-2 rounded-xl text-xs shrink-0">
              <span className="text-[#F2ECE5]/50 shrink-0">{isZh ? '平台' : 'Platform'}</span>
              <select
                value={previewPlatform}
                onChange={(e) => setPreviewPlatform(e.target.value)}
                className="bg-transparent text-[#F2ECE5] focus:outline-none cursor-pointer pr-1"
              >
                <option value="all" className="bg-[#181411] text-[#F2ECE5]">{isZh ? '全部平台' : 'All Platforms'}</option> 
                <option value="tiktok" className="bg-[#181411] text-[#F2ECE5]">TikTok</option>
                <option value="xiaohongshu" className="bg-[#181411] text-[#F2ECE5]">小红书</option>
                <option value="instagram" className="bg-[#181411] text-[#F2ECE5]">Instagram</option>
              </select>
            </div> */}

            {/* 赛道筛选 */}
            {/* <div className="flex items-center gap-2 bg-white/[0.03] border border-white/[0.08] px-3.5 py-2 rounded-xl text-xs shrink-0">
              <span className="text-[#F2ECE5]/50 shrink-0">{isZh ? '赛道' : 'Category'}</span>
              <select
                value={previewCategory}
                onChange={(e) => setPreviewCategory(e.target.value)}
                className="bg-transparent text-[#F2ECE5] focus:outline-none cursor-pointer pr-1"
              >
                <option value="all" className="bg-[#181411] text-[#F2ECE5]">{isZh ? '全部赛道' : 'All Categories'}</option>
                <option value="美妆" className="bg-[#181411] text-[#F2ECE5]">{isZh ? '美妆' : 'Beauty'}</option>
                <option value="3C" className="bg-[#181411] text-[#F2ECE5]">3C</option>
                <option value="美食探店" className="bg-[#181411] text-[#F2ECE5]">{isZh ? '美食探店' : 'Food'}</option>
                <option value="AI内容" className="bg-[#181411] text-[#F2ECE5]">{isZh ? 'AI内容' : 'AI'}</option>
                <option value="剧情娱乐" className="bg-[#181411] text-[#F2ECE5]">{isZh ? '剧情娱乐' : 'Drama'}</option>
                <option value="穿搭" className="bg-[#181411] text-[#F2ECE5]">{isZh ? '穿搭' : 'Fashion'}</option>
                <option value="其他" className="bg-[#181411] text-[#F2ECE5]">{isZh ? '其他' : 'Other'}</option>
              </select>
            </div> */}

            {/* 粉丝量级筛选 (对齐后端 4 档原生枚举 + 全部) */}
            <div className="flex items-center gap-2 bg-white/[0.03] border border-white/[0.08] px-3.5 py-2 rounded-xl text-xs shrink-0">
              <span className="text-[#F2ECE5]/50 shrink-0">{isZh ? '粉丝量级' : 'Follower Tier'}</span>
              <select
                value={previewTier}
                onChange={(e) => setPreviewTier(e.target.value)}
                className="bg-transparent text-[#F2ECE5] focus:outline-none cursor-pointer pr-1"
              >
                <option value="all" className="bg-[#181411] text-[#F2ECE5]">{isZh ? '全部量级' : 'All Tiers'}</option>
                <option value="<10k" className="bg-[#181411] text-[#F2ECE5]">&lt; 10k ({isZh ? '1万以下' : '< 10k'})</option>
                <option value="10k-100k" className="bg-[#181411] text-[#F2ECE5]">10k-100k ({isZh ? '1-10万' : '10k-100k'})</option>
                <option value="100k-1M" className="bg-[#181411] text-[#F2ECE5]">100k-1M ({isZh ? '10-100万' : '100k-1M'})</option>
                <option value=">1M" className="bg-[#181411] text-[#F2ECE5]">&gt; 1M ({isZh ? '100万以上' : '> 1M'})</option>
              </select>
            </div>
 
          </div>  

          {/* 表头标题与统计信息 */}
          <div className="flex items-center justify-between gap-2 mt-5 mb-3">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-[#F2ECE5]">
                {isZh ? '达人列表' : 'Creator Directory'}
              </h3>
              <span className="text-xs text-[#F2ECE5]/50">
                {isUnlocked
                  ? (isZh ? `(共 ${talentsTotal.toLocaleString()} 位达人)` : `(${talentsTotal.toLocaleString()} creators)`)
                  : (isZh
                      ? `(优质试看样板 · 当前呈现 ${displayedTalents.length} 位 · 全量样板 ${talentsTotal} 位)`
                      : `(Sample Preview · ${displayedTalents.length} shown of ${talentsTotal} samples)`)}
              </span>
            </div>
            {talentsLoading && (
              <span className="text-xs text-[#FDBA74] flex items-center gap-1 font-mono">
                <Loader2 className="w-3 h-3 animate-spin" />
                <span>{isZh ? '加载中...' : 'Loading...'}</span>
              </span>
            )}
          </div>


          {/* 达人明细表格（固定高度滚动、独立滚动隔离 overscroll-contain、微光细滚动条、表头 Sticky 吸顶） */}
          <div
            data-lenis-prevent="true"
            data-lenis-prevent-wheel="true"
            data-lenis-prevent-touch="true"
            className="rounded-xl border border-white/[0.08] overflow-hidden bg-white/[0.01]"
          >
            <div
              data-lenis-prevent="true"
              data-lenis-prevent-wheel="true"
              data-lenis-prevent-touch="true"
              onWheel={(e) => e.stopPropagation()}
              onTouchMove={(e) => e.stopPropagation()}
              className="h-[380px] overflow-y-auto overflow-x-auto overscroll-contain [scrollbar-width:thin] [scrollbar-color:rgba(255,255,255,0.2)_transparent] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-track]:bg-white/[0.02] [&::-webkit-scrollbar-thumb]:bg-white/20 hover:[&::-webkit-scrollbar-thumb]:bg-[#F97316]/70 [&::-webkit-scrollbar-thumb]:rounded-full"
            >
              <table className="w-full text-left border-collapse">
                <thead className="sticky top-0 z-20 bg-[#141210] shadow-[0_1px_0_rgba(255,255,255,0.08)]">
                  <tr className="text-xs text-[#F2ECE5]/50 font-medium">
                    <th className="py-3 px-3.5">{isZh ? '达人/账号' : 'Creator / Handle'}</th>
                    <th className="py-3 px-3.5 text-center">{isZh ? '平台' : 'Platform'}</th>
                    <th className="py-3 px-3.5 text-right font-mono">{isZh ? '粉丝数' : 'Followers'}</th>
                    <th className="py-3 px-3.5 text-center">{isZh ? '赛道' : 'Category'}</th>
                    <th className="py-3 px-3.5 text-center">{isZh ? '地区' : 'Region'}</th>
                    <th className="py-3 px-3.5 text-right font-mono">{isZh ? '互动率' : 'Engagement'}</th>
                    <th className="py-3 px-3.5 text-right font-mono">{isZh ? '均播/曝光' : 'Avg Views'}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.04] text-xs">
                  {talentsLoading && displayedTalents.length === 0 ? (
                    [1, 2, 3, 4, 5, 6].map((i) => (
                      <tr key={i} className="animate-pulse">
                        <td className="py-3 px-3.5">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-white/[0.05]" />
                            <div className="space-y-1.5">
                              <div className="w-24 h-3.5 rounded bg-white/[0.05]" />
                              <div className="w-16 h-2.5 rounded bg-white/[0.03]" />
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-3.5 text-center"><div className="w-12 h-4 rounded-full bg-white/[0.04] mx-auto" /></td>
                        <td className="py-3 px-3.5 text-right"><div className="w-14 h-3.5 rounded bg-white/[0.05] ml-auto" /></td>
                        <td className="py-3 px-3.5 text-center"><div className="w-16 h-4 rounded bg-white/[0.04] mx-auto" /></td>
                        <td className="py-3 px-3.5 text-center"><div className="w-10 h-3 rounded bg-white/[0.04] mx-auto" /></td>
                        <td className="py-3 px-3.5 text-right"><div className="w-12 h-3.5 rounded bg-white/[0.05] ml-auto" /></td>
                        <td className="py-3 px-3.5 text-right"><div className="w-14 h-3.5 rounded bg-white/[0.05] ml-auto" /></td>
                      </tr>
                    ))
                  ) : displayedTalents.length > 0 ? (
                    displayedTalents.map((talent) => {
                      const pFlag = talent.platform_flag || 'douyin';
                      const meta = PLATFORM_META[pFlag] || { label: pFlag, color: '#F97316', bg: 'rgba(249,115,22,0.15)' };

                      return (
                        <tr
                          key={talent.id}
                          className="hover:bg-white/[0.03] transition-colors group"
                        >
                          {/* 达人姓名与 Handle (支持 profile_url 外链跳转) */}
                          <td className="py-3 px-3.5">
                            <div className="flex items-center gap-2.5">
                              {talent.avatar_url ? (
                                <img
                                  src={talent.avatar_url}
                                  alt={talent.talent_name}
                                  className="w-8 h-8 rounded-full object-cover shrink-0 border border-white/10"
                                  onError={(e) => {
                                    (e.currentTarget as HTMLElement).style.display = 'none';
                                  }}
                                />
                              ) : (
                                <div className="w-8 h-8 rounded-full bg-white/[0.06] border border-white/10 flex items-center justify-center text-[11px] font-bold text-[#FDBA74] shrink-0">
                                  {talent.talent_name?.charAt(0)?.toUpperCase() || 'T'}
                                </div>
                              )}
                              <div className="min-w-0">
                                <div className="flex items-center gap-1">
                                  {talent.profile_url ? (
                                    <a
                                      href={talent.profile_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-sm font-semibold text-[#FDBA74] group-hover:text-amber-300 transition-colors truncate hover:underline inline-flex items-center gap-1"
                                      title={talent.talent_name}
                                    >
                                      <span className="truncate">{talent.talent_name}</span>
                                      <ExternalLink className="w-3 h-3 opacity-50 shrink-0 group-hover:opacity-100 transition-opacity" />
                                    </a>
                                  ) : (
                                    <span className="text-sm font-semibold text-[#FDBA74] truncate">
                                      {talent.talent_name}
                                    </span>
                                  )}
                                </div>
                                <div className="text-[11px] text-[#F2ECE5]/40 font-mono truncate">
                                  {talent.platform_handle}
                                </div>
                              </div>
                            </div>
                          </td>

                          {/* 平台徽标 */}
                          <td className="py-3 px-3.5 text-center">
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold"
                              style={{ backgroundColor: meta.bg, color: meta.color }}
                            >
                              {meta.label}
                            </span>
                          </td>

                          {/* 粉丝数 */}
                          <td className="py-3 px-3.5 text-right font-mono text-[#F2ECE5]/80 font-medium" title={talent.follower_count?.toLocaleString()}>
                            {formatCompactNumber(talent.follower_count, language)}
                          </td>

                          {/* 赛道标签组 */}
                          <td className="py-3 px-3.5 text-center">
                            <div className="flex flex-wrap items-center justify-center gap-1 max-w-[150px] mx-auto">
                              {talent.content_categories && talent.content_categories.length > 0 ? (
                                talent.content_categories.slice(0, 2).map((cat, idx) => (
                                  <span
                                    key={idx}
                                    className="px-1.5 py-0.5 rounded-md bg-white/[0.04] border border-white/[0.06] text-[#F2ECE5]/75 text-[10px]"
                                  >
                                    {cat}
                                  </span>
                                ))
                              ) : (
                                <span className="text-[#F2ECE5]/40 text-[11px]">-</span>
                              )}
                              {talent.content_categories && talent.content_categories.length > 2 && (
                                <span className="text-[10px] text-[#F2ECE5]/40">+{talent.content_categories.length - 2}</span>
                              )}
                            </div>
                          </td>

                          {/* 地区 */}
                          <td className="py-3 px-3.5 text-center text-[#F2ECE5]/70">
                            <span className="font-mono text-xs">{talent.country || '-'}</span>
                            {talent.account_region && (
                              <span className="ml-1 text-[10px] text-[#F2ECE5]/40">
                                ({talent.account_region === 'overseas' ? (isZh ? '海外' : 'Overseas') : (isZh ? '国内' : 'China')})
                              </span>
                            )}
                          </td>

                          {/* 互动率 */}
                          <td className="py-3 px-3.5 text-right font-mono font-medium">
                            <span className={typeof talent.engagement_rate === 'number' && talent.engagement_rate >= 5 ? 'text-emerald-400 font-semibold' : 'text-[#F2ECE5]/90'}>
                              {typeof talent.engagement_rate === 'number' ? `${talent.engagement_rate.toFixed(2)}%` : '-'}
                            </span>
                          </td>

                          {/* 均播/曝光 */}
                          <td className="py-3 px-3.5 text-right font-mono text-[#F2ECE5]/80 font-medium" title={talent.avg_play_count != null ? talent.avg_play_count.toLocaleString() : undefined}>
                            {typeof talent.avg_play_count === 'number' ? formatCompactNumber(talent.avg_play_count, language) : '-'}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={7} className="py-12 text-center text-xs text-[#F2ECE5]/40">
                        {talentsError
                          ? (isZh ? '达人列表加载异常，请点击搜索重试' : 'Failed to load creators, click search to retry')
                          : (isZh ? '暂无匹配的达人数据，请尝试调整筛选条件或搜索关键词' : 'No matching creators found.')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* 分页控制栏（仅在全量解锁模式且记录数超过 20 时展示翻页） */}
            {isUnlocked && talentsTotal > 20 && (
              <div className="px-5 py-3.5 bg-[#141210]/95 border-t border-white/[0.06] flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
                <div className="text-[#F2ECE5]/50">
                  {isZh
                    ? `第 ${(talentsPage - 1) * 20 + 1} - ${Math.min(talentsPage * 20, talentsTotal)} 条，共 ${talentsTotal.toLocaleString()} 位达人`
                    : `Showing ${(talentsPage - 1) * 20 + 1} - ${Math.min(talentsPage * 20, talentsTotal)} of ${talentsTotal.toLocaleString()} creators`}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={talentsPage <= 1 || talentsLoading}
                    onClick={() => loadTalents(talentsPage - 1)}
                    className="px-2.5 py-1 rounded-lg border border-white/10 bg-white/[0.03] hover:bg-white/[0.08] disabled:opacity-30 disabled:cursor-not-allowed transition-all text-[#F2ECE5] flex items-center gap-1 cursor-pointer"
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                    <span>{isZh ? '上一页' : 'Prev'}</span>
                  </button>
                  <span className="font-mono text-[#FDBA74] px-1 font-semibold">
                    {talentsPage} / {Math.ceil(talentsTotal / 20)}
                  </span>
                  <button
                    type="button"
                    disabled={talentsPage >= Math.ceil(talentsTotal / 20) || talentsLoading}
                    onClick={() => loadTalents(talentsPage + 1)}
                    className="px-2.5 py-1 rounded-lg border border-white/10 bg-white/[0.03] hover:bg-white/[0.08] disabled:opacity-30 disabled:cursor-not-allowed transition-all text-[#F2ECE5] flex items-center gap-1 cursor-pointer"
                  >
                    <span>{isZh ? '下一页' : 'Next'}</span>
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}

            {/* 未解锁状态下的底部常驻精简解锁提示条 */}
            {!isUnlocked && (
              <div className="px-5 py-3 bg-[#141210]/95 border-t border-white/[0.06] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#F97316]/15 border border-[#F97316]/30 flex items-center justify-center shrink-0 shadow-[0_0_10px_rgba(249,115,22,0.15)]">
                    <Lock className="w-4 h-4 text-[#F97316]" />
                  </div>
                  <div>
                    <div className="font-semibold text-[#F2ECE5] text-xs">
                      {!isLoggedIn
                        ? (isZh ? '登录 ORAN MED 查看更多达人资源库' : 'Sign in to ORAN MED for full Creator Library')
                        : (isZh ? '达人精选样板体验中' : 'Sample Preview Mode Active')}
                    </div>
                    <div className="text-[11px] text-[#F2ECE5]/50 mt-0.5">
                      {!isLoggedIn
                        ? (isZh
                            ? '获取联系方式、报价、CPM、综合评分等更多数据，开启高效的达人合作'
                            : 'Get direct contacts, quotes, CPM, comprehensive ratings, and unlock efficient partnerships.')
                        : (isZh
                            ? '当前已呈现 4 个量级共 80 位高粘性达人样本；开通专业权限或登录已授权账号，即可解锁 5,000+ 达人全库检索、深度筛选与翻页'
                            : 'Showing 80 top sample creators across 4 tiers. Unlock professional access or sign in with an authorized account for 5,000+ creators, advanced filters, and pagination.')}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleTriggerLogin}
                  className="px-4 py-2 rounded-xl bg-gradient-to-r from-[#F97316] to-[#EA580C] hover:from-[#EA580C] hover:to-[#C2410C] text-white text-xs font-semibold shadow-[0_0_12px_rgba(249,115,22,0.3)] transition-all shrink-0 cursor-pointer self-end sm:self-center"
                >
                  {!isLoggedIn
                    ? (isZh ? '立即登录解锁' : 'Sign In to Unlock')
                    : (isZh ? '切换授权账号' : 'Switch Authorized Account')}
                </button>
              </div>
            )}
          </div>
        </motion.div>      
        {/* ============================================================== */}
        {/* 组件 7：达人质量看板 - 粉丝量与互动率对数散点图 (仅真实数据成功时渲染) */}
        {/* ============================================================== */}
        {statsData && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.08] mb-8"
          >
          {/* 头部标题与控制栏 */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Target className="w-4 h-4 text-[#F97316]" />
                <h3 className="text-sm font-semibold text-[#F2ECE5]">
                  {isZh ? '部分粉丝量与互动率对数散点图' : 'Follower vs. Engagement Rate Scatter'}
                </h3>
                <span className="text-xs text-[#F2ECE5]/40 hidden sm:inline">
                  ({isZh ? `对数坐标` : `Log-scale `})
                </span>
              </div>
              <p className="text-[11px] text-[#F2ECE5]/40">
                {isZh
                  ? '横轴为对数粉丝体量 (Log Scale)，纵轴为实际互动率；四象限直观区分高爆达人、粘性黑马与常规基石'
                  : 'X-axis uses log10 scale for followers; Y-axis indicates true engagement rate'}
              </p>
            </div>

            {/* 平台交互筛选器（微型按钮组，纯净质感） */}
            <div className="flex items-center gap-1.5 p-1 rounded-xl bg-white/[0.02] border border-white/[0.06] overflow-x-auto">
              <button
                type="button"
                onClick={() => setScatterPlatform('all')}
                className={`text-xs px-2.5 py-1 rounded-lg transition-all ${
                  scatterPlatform === 'all'
                    ? 'bg-white/[0.1] text-[#F2ECE5] font-medium'
                    : 'text-[#F2ECE5]/50 hover:text-[#F2ECE5]'
                }`}
              >
                {isZh ? '全部平台' : 'All'}
              </button>
              {availableScatterPlatforms.map((p) => {
                const active = scatterPlatform === p.code;
                return (
                  <button
                    key={p.code}
                    type="button"
                    onClick={() => setScatterPlatform(p.code)}
                    className={`text-xs px-2.5 py-1 rounded-lg transition-all flex items-center gap-1.5 shrink-0 ${
                      active
                        ? 'bg-white/[0.1] text-[#F2ECE5] font-medium'
                        : 'text-[#F2ECE5]/50 hover:text-[#F2ECE5]'
                    }`}
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ backgroundColor: p.color }}
                    />
                    <span>{p.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* 散点图主体区域 */}
          <div className="h-80 w-full my-3">
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart margin={{ top: 20, right: 30, bottom: 20, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff0d" />
                <XAxis
                  type="number"
                  dataKey="log_follower"
                  domain={[3, 6.2]}
                  ticks={[3, 4, 5, 6]}
                  tickFormatter={(v) => ({ 3: '1,000', 4: '1.0万', 5: '10.0万', 6: '100.0万' }[v] || '')}
                  stroke="#ffffff40"
                  fontSize={11}
                  tickLine={false}
                  axisLine={{ stroke: '#ffffff15' }}
                  name="粉丝量 (对数)"
                />
                <YAxis
                  type="number"
                  dataKey="engagement_rate"
                  domain={[0, 80]}
                  ticks={[0, 20, 40, 60, 80]}
                  tickFormatter={(v) => `${v.toFixed(1)}%`}
                  stroke="#ffffff40"
                  fontSize={11}
                  tickLine={false}
                  axisLine={{ stroke: '#ffffff15' }}
                  name="互动率"
                />
                <ZAxis range={[32, 32]} />
                <ReferenceLine
                  y={15}
                  stroke="#F97316"
                  strokeDasharray="4 4"
                  strokeOpacity={0.4}
                  label={{
                    value: isZh ? '高粘性基准 (15%)' : 'High Engagement (15%)',
                    fill: '#F97316',
                    fontSize: 10,
                    position: 'insideTopRight',
                  }}
                />
                <ReferenceLine
                  x={5}
                  stroke="#ffffff"
                  strokeDasharray="4 4"
                  strokeOpacity={0.2}
                  label={{
                    value: isZh ? '腰部分水岭 (10万)' : 'Mid-Tier (100k)',
                    fill: '#ffffff60',
                    fontSize: 10,
                    position: 'insideTopLeft',
                  }}
                />
                <Scatter
                  name="Talents"
                  data={filteredScatterPoints}
                >
                  {filteredScatterPoints.map((entry, index) => {
                    const platformMeta = PLATFORM_META[entry.platform_flag] || { color: '#F97316' };
                    return (
                      <Cell
                        key={`scatter-${index}`}
                        fill={platformMeta.color}
                        fillOpacity={0.65}
                        stroke={platformMeta.color}
                        strokeWidth={0.5}
                        strokeOpacity={0.8}
                      />
                    );
                  })}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          </div>

          {/* 四象限业务指引条（极简统一纯净色系） */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5 pt-3 border-t border-white/[0.06]">
            {/* 象限 1：高粘性腰尾黑马 */}
            <div className="p-2.5 rounded-xl bg-white/[0.015] border border-white/[0.06]">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-[#F2ECE5] mb-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#FDBA74]" />
                <span>{isZh ? '高粘性腰尾黑马' : 'Dark Horse Tier'}</span>
              </div>
              <div className="text-[11px] text-[#F2ECE5]/50 leading-relaxed">
                {isZh ? '粉丝 < 10万 · 互动率 ≥ 15%，垂类黏性极高，高 ROI 首选' : '<100k fans, ≥15% eng rate, best ROI'}
              </div>
            </div>

            {/* 象限 2：头部高爆顶流 */}
            <div className="p-2.5 rounded-xl bg-white/[0.015] border border-white/[0.06]">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-[#F2ECE5] mb-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#FDBA74]" />
                <span>{isZh ? '头部高爆顶流' : 'Top Viral'}</span>
              </div>
              <div className="text-[11px] text-[#F2ECE5]/50 leading-relaxed">
                {isZh ? '粉丝 ≥ 10万 · 互动率 ≥ 15%，全网破圈力与声量放大引擎' : '≥100k fans, ≥15% eng rate, breakout viral'}
              </div>
            </div>

            {/* 象限 3：大盘常规基石池 */}
            <div className="p-2.5 rounded-xl bg-white/[0.015] border border-white/[0.06]">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-[#F2ECE5] mb-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#FDBA74]" />
                <span>{isZh ? '大盘常规基石池' : 'Baseline Pool'}</span>
              </div>
              <div className="text-[11px] text-[#F2ECE5]/50 leading-relaxed">
                {isZh ? '粉丝 < 10万 · 互动率 < 15%，密集分布在腰尾部的主力承载池' : '<100k fans, <15% eng, core workforce'}
              </div>
            </div>

            {/* 象限 4：品牌泛曝光沉淀 */}
            <div className="p-2.5 rounded-xl bg-white/[0.015] border border-white/[0.06]">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-[#F2ECE5] mb-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#FDBA74]" />
                <span>{isZh ? '品牌泛曝光沉淀' : 'Brand Exposure'}</span>
              </div>
              <div className="text-[11px] text-[#F2ECE5]/50 leading-relaxed">
                {isZh ? '粉丝 ≥ 10万 · 互动率 < 15%，重在公域大曝光，需防范僵尸粉' : '≥100k fans, <15% eng, broad exposure'}
              </div>
            </div>
          </div>
        </motion.div>
      )}
      </div>
    </div>
  );
};

export default InfluencerLibraryPage;
